import React, { useState } from 'react';
import { MIcon } from '../../lib/micon';
import { schuljahrKurz } from '../../lib/schuljahr';
import { Schueler, FehlzeitenVorjahr } from '../../types';

// Welle J1: Fehlzeiten vergangener Schuljahre — nur lesen. Sie zählen nicht in
// Kennzahlen/§68 des laufenden Schuljahrs, bleiben aber als Nachweis sichtbar.

function datumDE(iso: string) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso || '–';
  const [j, m, t] = iso.split('-');
  return `${t}.${m}.${j}`;
}

const sjTitel = (label: string) => (label.includes('/') ? `Schuljahr ${schuljahrKurz(label)}` : label);

function std(n: number) {
  return `${Math.round(n * 10) / 10} Std.`;
}

function Klappkopf({ offen, onClick, titel, sub }: { offen: boolean; onClick: () => void; titel: string; sub: string }) {
  return (
    <button onClick={onClick} aria-expanded={offen} className="ld-fz-ok-toggle"
      style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left', padding: '13px 16px', background: 'var(--ld-bg-card)', border: 'none', borderRadius: 'var(--ld-radius-lg)', boxShadow: 'var(--ld-shadow-elev-low)', cursor: 'pointer' }}>
      <MIcon name="history" size={18} color="var(--ld-text-secondary)" />
      <span style={{ fontWeight: 700, fontSize: 13, color: 'var(--ld-text-primary)' }}>{titel}</span>
      <span style={{ fontSize: 12, color: 'var(--ld-text-secondary)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>· {sub}</span>
      <MIcon name="expand_more" size={20} color="var(--ld-text-secondary)" style={{ marginLeft: 'auto', transform: offen ? 'rotate(180deg)' : 'none', transition: '.15s' }} />
    </button>
  );
}

const hinweisStil: React.CSSProperties = { fontSize: 12, color: 'var(--ld-text-tertiary)', margin: '0 2px 10px', lineHeight: 1.4 };

// ── Schülerprofil: Vorjahre mit Summen + Einzeleinträgen ─────────────────────
export function FehlzeitenVorjahreProfil({ vorjahre }: { vorjahre?: FehlzeitenVorjahr[] }) {
  const [offen, setOffen] = useState(false);
  if (!vorjahre || vorjahre.length === 0) return null;
  const sub = vorjahre.map(v => `${v.label}: ${std(v.stunden_gesamt)}`).join(' · ');
  return (
    <div style={{ marginTop: 16 }}>
      <Klappkopf offen={offen} onClick={() => setOffen(o => !o)} titel="Vorjahre" sub={sub} />
      {offen && (
        <div style={{ marginTop: 11 }}>
          <p style={hinweisStil}>Nur zur Ansicht. Diese Fehlzeiten zählen nicht zu den Kennzahlen und zur §68-Schwelle im laufenden Schuljahr.</p>
          {vorjahre.map(v => (
            <div key={v.label} style={{ background: 'var(--ld-bg-card)', borderRadius: 'var(--ld-radius-lg)', boxShadow: 'var(--ld-shadow-elev-low)', padding: '14px 16px', marginBottom: 11 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--ld-text-primary)' }}>{sjTitel(v.label)}</span>
                <span style={{ fontSize: 12, color: 'var(--ld-text-secondary)' }}>
                  {v.tage_gesamt} Tage · {std(v.stunden_gesamt)} ({v.stunden_unentschuldigt} unentsch.)
                </span>
              </div>
              <div style={{ marginTop: 8 }}>
                {v.eintraege.map(e => (
                  <div key={e.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderTop: '1px solid var(--ld-fz-divider, #F0F0F5)', fontSize: 13 }}>
                    <span style={{ width: 84, flexShrink: 0, color: 'var(--ld-text-primary)', fontVariantNumeric: 'tabular-nums' }}>{datumDE(e.datum)}</span>
                    <span style={{ flex: 1, minWidth: 0, color: 'var(--ld-text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {e.stunden != null ? std(e.stunden) : e.tage != null ? `${e.tage} Tag${e.tage === 1 ? '' : 'e'}` : '–'}
                      {e.grund ? ` · ${e.grund}` : ''}
                    </span>
                    <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 'var(--ld-radius-pill)', flexShrink: 0,
                      background: e.entschuldigt ? 'var(--ld-fz-gruen-bg, #E0F2E8)' : 'var(--ld-fz-amber-bg, #FBEFD9)',
                      color: e.entschuldigt ? 'var(--ld-fz-gruen, #2F8B4E)' : 'var(--ld-fz-amber, #A86A0C)' }}>
                      {e.entschuldigt ? 'entsch.' : 'unentsch.'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── FehlzeitenScreen: je Schüler eine Summenzeile pro Vorjahr ───────────────
export function FehlzeitenVorjahreUebersicht({ schueler }: { schueler: Schueler[] }) {
  const [offen, setOffen] = useState(false);
  const mitVorjahren = schueler.filter(s => (s.fehlzeiten_vorjahre?.length ?? 0) > 0);
  if (mitVorjahren.length === 0) return null;
  return (
    <div style={{ marginBottom: 14 }}>
      <Klappkopf offen={offen} onClick={() => setOffen(o => !o)} titel="Vorjahre"
        sub={`${mitVorjahren.length} Schüler mit Einträgen · nur lesen`} />
      {offen && (
        <div style={{ marginTop: 11, background: 'var(--ld-bg-card)', borderRadius: 'var(--ld-radius-lg)', boxShadow: 'var(--ld-shadow-elev-low)', padding: '6px 16px' }}>
          <p style={{ ...hinweisStil, margin: '8px 0' }}>Zählen nicht zur §68-Schwelle im laufenden Schuljahr.</p>
          {mitVorjahren.map(s => (
            <div key={s.id} style={{ display: 'flex', alignItems: 'baseline', gap: 10, padding: '9px 0', borderTop: '1px solid var(--ld-fz-divider, #F0F0F5)', flexWrap: 'wrap' }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--ld-text-primary)', flex: '1 1 140px', minWidth: 0 }}>
                {s.name} <span style={{ fontWeight: 400, color: 'var(--ld-text-tertiary)' }}>{s.klasse}</span>
              </span>
              <span style={{ fontSize: 12, color: 'var(--ld-text-secondary)' }}>
                {s.fehlzeiten_vorjahre!.map(v => `${v.label}: ${std(v.stunden_gesamt)} (${v.stunden_unentschuldigt} unentsch.)`).join(' · ')}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
